import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { appointmentTypeLabel } from '../../utils/appointmentTypes';
import DocumentDownloadMenu from '../../components/common/DocumentDownloadMenu';

const API_BASE = '/admin/medical-records';

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const nameOf = (entity) => {
  const p = entity?.user?.profile || entity?.profile || {};
  return [p.firstName, p.lastName].filter(Boolean).join(' ') || '—';
};

// ── sub-componentes ──────────────────────────────────────────────────────────
function Badge({ text }) {
  return (
    <span className="inline-block bg-teal-100 text-teal-800 text-xs font-medium px-2 py-0.5 rounded-full">
      {text}
    </span>
  );
}

function FilterBar({ filters, onChange, onSearch, loading }) {
  return (
    <form
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
      onSubmit={(e) => { e.preventDefault(); onSearch(); }}
    >
      <input
        className="input-field"
        placeholder="Nombre paciente"
        value={filters.patient}
        onChange={(e) => onChange('patient', e.target.value)}
      />
      <input
        className="input-field"
        placeholder="Nombre doctor"
        value={filters.doctor}
        onChange={(e) => onChange('doctor', e.target.value)}
      />
      <div className="flex gap-2">
        <input
          type="date"
          className="input-field flex-1"
          value={filters.dateFrom}
          onChange={(e) => onChange('dateFrom', e.target.value)}
          title="Desde"
        />
        <input
          type="date"
          className="input-field flex-1"
          value={filters.dateTo}
          onChange={(e) => onChange('dateTo', e.target.value)}
          title="Hasta"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition disabled:opacity-50"
        >
          {loading ? 'Buscando…' : 'Buscar'}
        </button>
        <button
          type="button"
          onClick={() => { onChange('__reset'); onSearch(true); }}
          className="px-3 py-2 text-gray-500 hover:text-gray-800 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 transition"
          title="Limpiar filtros"
        >
          ✕
        </button>
      </div>
    </form>
  );
}

function RecordRow({ record, onView, onPrint }) {
  return (
    <tr className="hover:bg-gray-50 transition">
      <td className="px-4 py-3 text-sm">{fmt(record.createdAt)}</td>
      <td className="px-4 py-3 text-sm font-medium">{nameOf(record.patient)}</td>
      <td className="px-4 py-3 text-sm">{nameOf(record.doctor)}</td>
      <td className="px-4 py-3 text-sm">
        <span className="text-gray-600 truncate block max-w-xs">
          {record.diagnosis?.primary || '—'}
        </span>
      </td>
      <td className="px-4 py-3">
        {record.doctor?.specialization && <Badge text={record.doctor.specialization} />}
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-2">
          <button
            onClick={() => onView(record)}
            className="text-teal-600 hover:text-teal-800 text-sm font-medium transition"
          >
            Ver
          </button>
          <DocumentDownloadMenu
            record={record}
            label="PDF"
            busyLabel="PDF"
            className="relative inline-block"
            buttonClassName="text-blue-600 hover:text-blue-800 text-sm font-medium transition"
            onDownload={(doc) => onPrint(record._id, doc)}
          />
        </div>
      </td>
    </tr>
  );
}

function DetailModal({ record, onClose, onPrint }) {
  if (!record) return null;

  const patientProfile = record.patient?.user?.profile || {};
  const doctorProfile  = record.doctor?.user?.profile  || {};
  const pe = record.physicalExam;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl">
        <div className="bg-teal-700 text-white rounded-t-2xl px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-bold">Historia Clínica</h2>
            <p className="text-teal-200 text-xs mt-0.5">ID: {record._id}</p>
          </div>
          <div className="flex gap-3">
            <DocumentDownloadMenu
              record={record}
              label="🖨️ Imprimir PDF"
              busyLabel="🖨️ Imprimir PDF"
              className="relative inline-block"
              buttonClassName="bg-white/20 hover:bg-white/30 text-white text-sm px-3 py-1.5 rounded-lg transition flex items-center gap-1"
              onDownload={(doc) => onPrint(record._id, doc)}
            />
            <button onClick={onClose} className="text-white/70 hover:text-white text-xl leading-none">✕</button>
          </div>
        </div>

        <div className="p-6 space-y-6 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <InfoBox title="Paciente">
              <KV label="Nombre" v={[patientProfile.firstName, patientProfile.lastName].filter(Boolean).join(' ')} />
              <KV label="Email"  v={record.patient?.user?.email} />
              <KV label="Doc."   v={patientProfile.documentNumber} />
            </InfoBox>
            <InfoBox title="Médico">
              <KV label="Dr."          v={[doctorProfile.firstName, doctorProfile.lastName].filter(Boolean).join(' ')} />
              <KV label="Especialidad" v={record.doctor?.specialization} />
              <KV label="Reg."         v={record.doctor?.licenseNumber} />
            </InfoBox>
          </div>

          {record.appointment?.scheduledDate && (
            <InfoBox title="Consulta">
              <KV label="Fecha" v={fmt(record.appointment.scheduledDate)} />
              <KV label="Modalidad" v={appointmentTypeLabel(record.appointment.type, 'Telemedicina')} />
            </InfoBox>
          )}

          {record.chiefComplaint      && <Section title="Motivo de consulta" text={record.chiefComplaint} />}
          {record.currentIllness      && <Section title="Enfermedad actual"  text={record.currentIllness} />}
          {record.pathologicalHistory && <Section title="Antecedentes"       text={record.pathologicalHistory} />}

          {pe && (
            <InfoBox title="Examen físico">
              <div className="grid grid-cols-3 gap-2">
                {pe.weight?.value    && <KV label="Peso"      v={`${pe.weight.value} ${pe.weight.unit}`} />}
                {pe.height?.value    && <KV label="Talla"     v={`${pe.height.value} ${pe.height.unit}`} />}
                {pe.bmi              && <KV label="IMC"       v={pe.bmi.toFixed(1)} />}
                {pe.bloodPressure?.systolic && <KV label="P.A." v={`${pe.bloodPressure.systolic}/${pe.bloodPressure.diastolic} mmHg`} />}
                {pe.heartRate        && <KV label="FC"        v={`${pe.heartRate} bpm`} />}
                {pe.temperature      && <KV label="Temp."     v={`${pe.temperature} °C`} />}
                {pe.oxygenSaturation && <KV label="Sat. O₂"  v={`${pe.oxygenSaturation}%`} />}
              </div>
              {pe.notes && <p className="mt-2 text-gray-600">{pe.notes}</p>}
            </InfoBox>
          )}

          {record.analysis && <Section title="Análisis" text={record.analysis} />}

          {record.diagnosis && (
            <InfoBox title="Diagnóstico">
              <KV label="Principal" v={record.diagnosis.primary} />
              {record.diagnosis.secondary?.map((d, i) => (
                <p key={i} className="text-gray-600">• {d}</p>
              ))}
            </InfoBox>
          )}

          {record.conduct?.medications?.length > 0 && (
            <InfoBox title="Medicamentos prescritos">
              {record.conduct.medications.map((m, i) => (
                <div key={i} className="mb-2 border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                  <p className="font-medium text-gray-800">{m.name}</p>
                  {m.dosage      && <p className="text-gray-500">Dosis: {m.dosage}</p>}
                  {m.frequency   && <p className="text-gray-500">Frecuencia: {m.frequency}</p>}
                  {m.duration    && <p className="text-gray-500">Duración: {m.duration}</p>}
                  {m.instructions && <p className="text-gray-500 italic">{m.instructions}</p>}
                </div>
              ))}
            </InfoBox>
          )}

          {record.conduct?.recommendations && (
            <Section title="Recomendaciones" text={record.conduct.recommendations} />
          )}

          {record.conduct?.followUp?.required && (
            <InfoBox title="Seguimiento">
              {record.conduct.followUp.recommendedDate && (
                <KV label="Fecha sugerida" v={fmt(record.conduct.followUp.recommendedDate)} />
              )}
              {record.conduct.followUp.instructions && (
                <p className="text-gray-600 mt-1">{record.conduct.followUp.instructions}</p>
              )}
            </InfoBox>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoBox({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-100 overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
      </div>
      <div className="px-4 py-3 space-y-1">{children}</div>
    </div>
  );
}

function KV({ label, v }) {
  if (!v) return null;
  return (
    <p>
      <span className="text-gray-400">{label}: </span>
      <span className="text-gray-800 font-medium">{v}</span>
    </p>
  );
}

function Section({ title, text }) {
  return (
    <InfoBox title={title}>
      <p className="text-gray-700 leading-relaxed whitespace-pre-line">{text}</p>
    </InfoBox>
  );
}

// ── página principal ─────────────────────────────────────────────────────────
const EMPTY_FILTERS = { patient: '', doctor: '', dateFrom: '', dateTo: '' };

export default function MedicalRecordsPage() {
  const [records,  setRecords]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [pages,    setPages]    = useState(1);
  const [page,     setPage]     = useState(1);
  const [filters,  setFilters]  = useState(EMPTY_FILTERS);
  const [loading,  setLoading]  = useState(false);
  const [selected, setSelected] = useState(null);
  const [error,    setError]    = useState('');

  // debounce ref para búsqueda automática mientras escribe
  const debounceRef = useRef(null);

  const fetchRecords = useCallback(async (overrideFilters, overridePage = 1) => {
    setLoading(true);
    setError('');
    const f = overrideFilters ?? filters;
    const p = overridePage;
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (f.dateFrom) params.set('dateFrom', f.dateFrom);
      if (f.dateTo)   params.set('dateTo',   f.dateTo);
      // Ahora estos se envían al backend (aggregation pipeline)
      if (f.patient.trim()) params.set('patient', f.patient.trim());
      if (f.doctor.trim())  params.set('doctor',  f.doctor.trim());

      const { data } = await api.get(`${API_BASE}?${params}`);
      if (data.success) {
        setRecords(data.data);
        setTotal(data.total);
        setPages(data.pages);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar historias clínicas');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Carga inicial y cuando cambia página
  useEffect(() => {
    fetchRecords(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = (reset = false) => {
    const newFilters = reset ? EMPTY_FILTERS : filters;
    if (reset) setFilters(EMPTY_FILTERS);
    setPage(1);
    fetchRecords(newFilters, 1);
  };

  const handleFilterChange = (key, value) => {
    if (key === '__reset') {
      setFilters(EMPTY_FILTERS);
      return;
    }
    const updated = { ...filters, [key]: value };
    setFilters(updated);

    // Debounce automático para campos de texto (patient / doctor)
    if (key === 'patient' || key === 'doctor') {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setPage(1);
        fetchRecords(updated, 1);
      }, 400);
    }
  };

  // doc: 'historia' | 'medicamentos' | 'examenes' | 'incapacidad'. Viaja al pedir
  // el token y otra vez en la URL: el backend los compara y un token de un
  // documento no sirve para otro.
  const handlePrint = async (id, doc = 'historia') => {
    try {
      const { data } = await api.post(`/admin/medical-records/${id}/generate-token`, { doc });
      if (!data.success) { alert('No se pudo generar el enlace de descarga'); return; }
      const base = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
      window.open(`${base}/api/admin/medical-records/${id}/print?dl_token=${data.token}&doc=${doc}`, '_blank');
    } catch (err) {
      console.error('handlePrint:', err);
      alert('Error al generar el enlace de descarga');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Historias Clínicas</h1>
          <p className="text-gray-500 text-sm mt-1">
            {total} registro{total !== 1 ? 's' : ''} en total
          </p>
        </div>

        <FilterBar filters={filters} onChange={handleFilterChange} onSearch={handleSearch} loading={loading} />

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading && records.length === 0 ? (
            <div className="text-center py-16 text-gray-400">Cargando…</div>
          ) : records.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-4xl mb-3">📋</p>
              <p className="text-gray-500">No se encontraron historias clínicas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Fecha', 'Paciente', 'Médico', 'Diagnóstico', 'Especialidad', 'Acciones'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {records.map((r) => (
                    <RecordRow
                      key={r._id}
                      record={r}
                      onView={setSelected}
                      onPrint={handlePrint}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <button
              disabled={page === 1 || loading}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition"
            >
              ← Anterior
            </button>
            <span className="px-3 py-1.5 text-sm text-gray-600">
              Página {page} de {pages}
            </span>
            <button
              disabled={page === pages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition"
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>

      <DetailModal record={selected} onClose={() => setSelected(null)} onPrint={handlePrint} />

      <style>{`
        .input-field {
          width: 100%;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 0.45rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s;
        }
        .input-field:focus { border-color: #0d9488; box-shadow: 0 0 0 2px #ccfbf1; }
      `}</style>
    </div>
  );
}